/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { ChangeEvent, useState } from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { useIntl } from 'react-intl';

import { MenuItem, Select } from "@material-ui/core";
import { ApplicationState, UserReducer } from "../../../data/interfaces";

import { createSubscription } from "../../../data/operations";
import PrimaryButton from "../../../elements/PrimaryButton";

import { useStyles } from "./styles";
import Settings from '../../../conf/settings';

interface ActionProps {
    applicationId?: string,
    apiId?: string,
    apiName?: string,
    apiVersion?: string,
    userReducer: UserReducer,
    throttlingPolicies: string[],
    alreadySubscribed?: boolean,
    dispatchCreateSubscription: (appId: string, organizationId: string, apiId: string,
                                 throttlingPolicy: string, versionRange?: string) => void,
}

function Actions(props: ActionProps) {
    const {
        applicationId,
        apiId,
        apiName,
        apiVersion,
        userReducer,
        throttlingPolicies,
        alreadySubscribed,
        dispatchCreateSubscription
    } = props;
    const classes = useStyles();
    const intl = useIntl();
    const { orgs: userOrganizations, selectedOrg } = userReducer;
    const [throttlingPolicy, setThrottlingPolicy] = useState(throttlingPolicies[0]);

    const subscriptionButtonLabel = intl.formatMessage({
        id: 'application.subscriptions.createDialog.subscribeButton.label',
        defaultMessage: 'Add'
    });

    const onThrottlingPolicyChange = (event: ChangeEvent<any>) => {
        const throttlingPolicy = event.target.value;
        setThrottlingPolicy(throttlingPolicy);
    };

    const subscribeToApi = () => {
        const organizationId = selectedOrg.uuid;
        dispatchCreateSubscription(
            applicationId as string,
            organizationId,
            apiId as string,
            throttlingPolicy,
            Settings.intelligentRoutingFeatureEnabled && apiVersion?.startsWith("v")
            ? apiVersion.split(".")[0] : undefined
        );
    };

    const SelectPolicy = (apiId: string, apiName: string, policies: string[]) => {
        return (
            <Select
                disabled={alreadySubscribed}
                variant="outlined"
                value={throttlingPolicy}
                onChange={onThrottlingPolicyChange}
                className={classes.selectDropDownSm}
                MenuProps={{
                    getContentAnchorEl: null,
                    classes: { paper: classes.dropdownStyle },
                    anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                    }
                }}
                data-testid={"throttling-policy-selector-" + apiName}
            >
                {policies?.map((policy) => (
                    <MenuItem key={apiId + policy} value={policy}>
                        {policy}
                    </MenuItem>
                ))}
            </Select>
        );
    };

    const SubscribeBtn = (apiName: string) => {
        return (
            <PrimaryButton
                disabled={alreadySubscribed}
                onClick={subscribeToApi}
                buttonLabel={subscriptionButtonLabel}
                className={classes.subscribeBtn}
                id={"add-api-" + apiName}
            />
        );
    };

    return (
        <div>
            <div className={!alreadySubscribed ? classes.actions : classNames(classes.actions, classes.disabledElement)}>
                {SelectPolicy(apiId as string, apiName as string, throttlingPolicies)}
                {SubscribeBtn(apiName as string)}
            </div>
        </div>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        userReducer: state.userReducer
    }
};

const mapDispatchToProps = {
    dispatchCreateSubscription: createSubscription,
};

export default connect(mapStateToProps, mapDispatchToProps)(Actions);
